import { z } from "zod";
import {
  FEEDBACK_DESCRIPTION_MAX,
  FEEDBACK_REASON_MAX,
  FEEDBACK_STATUSES,
  FEEDBACK_TYPES,
  operatorFeedbackItemSchema,
} from "./feedback.ts";
import { FEEDBACK_AREAS, FEEDBACK_SEVERITIES } from "./feedback-labels.ts";

/**
 * Feedback triage (openspec change `in-app-feedback`, slice 3, spec `feedback-triage`): operators
 * group reports, link client error fingerprints, set statuses and create a GitLab issue from a
 * preview. Everything here is the instance console's; reporters see only the status (and, for
 * `fixed`, the version) through "My feedback".
 */

/** A client error's grouping key (`errorFingerprint`): 16 lowercase hex characters. */
export const FEEDBACK_FINGERPRINT = /^[0-9a-f]{16}$/;
export const FEEDBACK_GROUP_TITLE_MAX = 200;
/** Reports moved in one request. */
export const FEEDBACK_ASSIGN_MAX = 50;
/** Reports listed in a group view (and suggested for it). */
export const FEEDBACK_GROUP_REPORTS_MAX = 200;
/** Groups the console lists: open ones first, then the most recently closed. */
export const FEEDBACK_GROUPS_MAX = 500;
/** Labels every feedback issue carries, plus the type's `kind::` label. */
export const FEEDBACK_ISSUE_SOURCE_LABEL = "source::feedback";

const fingerprintSchema = z.string().regex(FEEDBACK_FINGERPRINT);
const titleSchema = z.string().trim().min(1).max(FEEDBACK_GROUP_TITLE_MAX);
const summarySchema = z.string().trim().max(FEEDBACK_DESCRIPTION_MAX);
/** The "not planned" reason reporters read (feedback-reporter-status D5); empty clears it. */
const reasonSchema = z.string().trim().max(FEEDBACK_REASON_MAX);

/** A group in the console's list: the operator's title, never a reporter's words. */
export const feedbackGroupItemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  type: z.enum(FEEDBACK_TYPES),
  status: z.enum(FEEDBACK_STATUSES),
  issueNumber: z.number().int().positive().nullable(),
  /** The issue's web address, when the instance knows its GitLab project. */
  issueUrl: z.string().nullable(),
  reports: z.number().int().min(0),
  tenants: z.number().int().min(0),
  updatedAt: z.iso.datetime({ offset: true }),
  /** Set by an operator (feedback-triage-assist); absent from an older API. */
  severity: z.enum(FEEDBACK_SEVERITIES).nullable().optional(),
  area: z.enum(FEEDBACK_AREAS).nullable().optional(),
});
export type FeedbackGroupItem = z.infer<typeof feedbackGroupItemSchema>;
export const feedbackGroupListSchema = z.object({ groups: z.array(feedbackGroupItemSchema) });
export type FeedbackGroupList = z.infer<typeof feedbackGroupListSchema>;

/** One group: its summary, linked fingerprints, reports and the reports suggested for it. */
export const feedbackGroupSchema = feedbackGroupItemSchema.extend({
  summary: z.string(),
  /** The reason reporters read while the group is `not_planned` (absent from an older API). */
  reason: z.string().optional(),
  closedAt: z.iso.datetime({ offset: true }).nullable(),
  createdAt: z.iso.datetime({ offset: true }),
  fingerprints: z.array(fingerprintSchema),
  members: z.array(operatorFeedbackItemSchema),
  /** Ungrouped reports whose recent errors carry one of the linked fingerprints. */
  suggested: z.array(operatorFeedbackItemSchema),
});
export type FeedbackGroup = z.infer<typeof feedbackGroupSchema>;

export const createFeedbackGroupSchema = z.strictObject({
  title: titleSchema,
  summary: summarySchema.default(""),
  type: z.enum(FEEDBACK_TYPES),
  severity: z.enum(FEEDBACK_SEVERITIES).nullable().optional(),
  area: z.enum(FEEDBACK_AREAS).nullable().optional(),
  /** Reports to put in the new group straight away. */
  reportIds: z.array(z.uuid()).max(FEEDBACK_ASSIGN_MAX).default([]),
});
export type CreateFeedbackGroup = z.infer<typeof createFeedbackGroupSchema>;

export const updateFeedbackGroupSchema = z
  .strictObject({
    title: titleSchema.optional(),
    summary: summarySchema.optional(),
    type: z.enum(FEEDBACK_TYPES).optional(),
    status: z.enum(FEEDBACK_STATUSES).optional(),
    reason: reasonSchema.optional(),
    /** Null clears it (feedback-triage-assist). */
    severity: z.enum(FEEDBACK_SEVERITIES).nullable().optional(),
    area: z.enum(FEEDBACK_AREAS).nullable().optional(),
  })
  .refine((v) => Object.keys(v).length > 0, { message: "Nothing to change" });
export type UpdateFeedbackGroup = z.infer<typeof updateFeedbackGroupSchema>;

/** Puts reports in a group (moving them from another one), or out of any group (`null`). */
export const assignFeedbackSchema = z.strictObject({
  reportIds: z.array(z.uuid()).min(1).max(FEEDBACK_ASSIGN_MAX),
  groupId: z.uuid().nullable(),
});
export type AssignFeedback = z.infer<typeof assignFeedbackSchema>;

export const linkFingerprintSchema = z.strictObject({ fingerprint: fingerprintSchema });
export type LinkFingerprint = z.infer<typeof linkFingerprintSchema>;

/** A report's place in triage, for the report view: its group and the suggested ones. */
export const feedbackGroupingSchema = z.object({
  group: z.object({ id: z.uuid(), title: z.string() }).nullable(),
  /** Groups linked to a fingerprint of the report's recent errors (exact match, no model). */
  suggestions: z.array(z.object({ id: z.uuid(), title: z.string(), fingerprint: z.string() })),
  /**
   * Every group link of the report's fingerprints, its own group's included
   * (feedback-inbox-followups D3); absent from an older API.
   */
  fingerprintGroups: z
    .array(z.object({ fingerprint: z.string(), groupId: z.uuid(), title: z.string() }))
    .optional(),
});
export type FeedbackGrouping = z.infer<typeof feedbackGroupingSchema>;

/** Why the issue preview refuses creation; `excerpt` is from the operator's own text. */
export const ISSUE_TEXT_FINDINGS = [
  "email",
  "uuid",
  "quote",
  "copied",
  "mention",
  // The name of a tenant with a report in the group (the reason, feedback-reporter-status D5).
  "tenant",
] as const;
export type IssueTextFindingKind = (typeof ISSUE_TEXT_FINDINGS)[number];
export const issueTextFindingSchema = z.object({
  field: z.enum(["title", "summary", "reason"]),
  kind: z.enum(ISSUE_TEXT_FINDINGS),
  excerpt: z.string(),
});
export type IssueTextFinding = z.infer<typeof issueTextFindingSchema>;

/** Whether the instance can create issues: a token is stored, missing, or cannot be read. */
export const ISSUE_TRACKER_STATUSES = ["configured", "not_configured", "unavailable"] as const;
export type IssueTrackerStatus = (typeof ISSUE_TRACKER_STATUSES)[number];
export const issueTrackerStatusSchema = z.object({ status: z.enum(ISSUE_TRACKER_STATUSES) });

/** The exact issue an operator would create, and what stops it. */
export const issuePreviewSchema = z.object({
  title: z.string(),
  body: z.string(),
  labels: z.array(z.string()),
  findings: z.array(issueTextFindingSchema),
  /** Sent back to create: the issue is created only if it still renders to this preview. */
  digest: z.string().regex(/^[0-9a-f]{64}$/),
  tracker: z.enum(ISSUE_TRACKER_STATUSES),
});
export type IssuePreview = z.infer<typeof issuePreviewSchema>;

export const createIssueSchema = z.strictObject({ digest: z.string().regex(/^[0-9a-f]{64}$/) });
export const createdIssueSchema = z.object({
  number: z.number().int().positive(),
  url: z.string().nullable(),
});
export type CreatedIssue = z.infer<typeof createdIssueSchema>;

/**
 * The issues closed per release (`Closes`/`Fixes` footers, `scripts/changelog.ts`), bundled into
 * the API at build: `version` is a release (`2026.10.30`) or, for commits after the last one,
 * the build's own version. Oldest first.
 */
export const closedIssuesSchema = z.object({
  releases: z.array(
    z.object({ version: z.string(), issues: z.array(z.number().int().positive()) }),
  ),
});
export type ClosedIssues = z.infer<typeof closedIssuesSchema>;

/** The bundled manifest (a JSON string), or none when absent or malformed: no "fixed in". */
export function parseClosedIssues(value: string | undefined): ClosedIssues | undefined {
  if (!value) {
    return undefined;
  }
  try {
    const parsed = closedIssuesSchema.safeParse(JSON.parse(value));
    return parsed.success ? parsed.data : undefined;
  } catch {
    return undefined;
  }
}
